import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Edit3, MessageSquareText, PhoneCall, Plus, Search, UserCheck, Users } from 'lucide-react'
import { Sidebar } from '../components/Sidebar'
import { LiverFormModal } from '../components/LiverFormModal'
import {
  buildLiverDirectory,
  type LiverDirectoryAssignmentView,
  type LiverDirectoryFilterStatus
} from '../lib/liverDirectory'
import type { LiverFormValues } from '../lib/liverForm'
import type { Liver } from '../lib/supabase'
import { useAllAssignments } from '../lib/useAssignments'
import { useCreateLiver, useLiverDirectoryActivities, useLivers, useUpdateLiver } from '../lib/useLivers'
import { useMyManager } from '../lib/useManagerProfile'

const STATUS_LABEL: Record<LiverDirectoryFilterStatus, string> = {
  all: 'すべて',
  active: '稼働中',
  inactive: '休眠',
  graduated: '卒業'
}

const STATUS_TONE: Record<Exclude<LiverDirectoryFilterStatus, 'all'>, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  inactive: 'bg-amber-50 text-amber-700',
  graduated: 'bg-gray-100 text-gray-500'
}

export function LiversPage() {
  const livers = useLivers()
  const assignments = useAllAssignments()
  const activities = useLiverDirectoryActivities()
  const myManager = useMyManager()
  const createLiver = useCreateLiver()
  const updateLiver = useUpdateLiver()
  const [status, setStatus] = useState<LiverDirectoryFilterStatus>('all')
  const [assignmentView, setAssignmentView] = useState<LiverDirectoryAssignmentView>('all')
  const [searchText, setSearchText] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Liver | null>(null)
  const directory = useMemo(
    () => buildLiverDirectory(livers.data ?? [], {
      status,
      searchText,
      assignmentView,
      currentManagerId: myManager.data?.id ?? null,
      assignments: assignments.data ?? [],
      activities: activities.data ?? []
    }),
    [activities.data, assignmentView, assignments.data, livers.data, myManager.data?.id, searchText, status]
  )
  const loading = livers.isLoading || assignments.isLoading || activities.isLoading || myManager.isLoading
  const error = livers.error ?? assignments.error ?? activities.error ?? myManager.error

  async function handleCreate(values: LiverFormValues) {
    await createLiver.mutateAsync(values)
    setCreateOpen(false)
  }

  async function handleUpdate(values: LiverFormValues) {
    if (!editing) return
    await updateLiver.mutateAsync({ id: editing.id, values })
    setEditing(null)
  }

  return (
    <div className="flex min-h-full">
      <Sidebar />
      <main className="min-w-0 flex-1 bg-gray-50 px-4 py-6 pt-16 sm:px-6 md:pt-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">ライバー管理</h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                ライバーの登録状態、LINE ID、入退所日、メモを一覧で管理します。
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-sky-500 px-3 py-2 text-sm font-medium text-white hover:bg-sky-600"
            >
              <Plus className="h-4 w-4" />
              追加
            </button>
          </header>

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Metric label="全体" value={`${directory.counts.all}名`} tone="slate" />
            <Metric label="稼働中" value={`${directory.counts.active}名`} tone="emerald" />
            <Metric label="休眠" value={`${directory.counts.inactive}名`} tone="amber" />
            <Metric label="卒業" value={`${directory.counts.graduated}名`} tone="gray" />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="名前 / LINE ID / メモ"
                    className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  />
                </div>
                <div className="inline-flex w-fit rounded-lg bg-gray-100 p-1">
                  {([
                    ['all', 'すべて'],
                    ['mine', `自分の担当 ${directory.myAssignedCount}`]
                  ] as const).map(([view, label]) => (
                    <button
                      key={view}
                      type="button"
                      onClick={() => setAssignmentView(view)}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                        assignmentView === view ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(['all', 'active', 'inactive', 'graduated'] as const).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setStatus(item)}
                      className={`rounded-lg px-3 py-2 text-xs font-medium transition ${
                        status === item ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {STATUS_LABEL[item]}
                    </button>
                  ))}
                </div>
              </div>
              {loading && <p className="text-xs text-gray-400">読み込み中...</p>}
              {error && (
                <p className="text-xs text-red-600">
                  取得に失敗: {error instanceof Error ? error.message : String(error)}
                </p>
              )}
            </div>

            {directory.rows.length > 0 ? (
              <ul className="lg:divide-y lg:divide-gray-100">
                {directory.rows.map((row) => {
                  const original = (livers.data ?? []).find((liver) => liver.id === row.id) ?? null
                  return (
                    <li key={row.id} className="mx-3 my-3 grid gap-3 rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm lg:mx-0 lg:my-0 lg:grid-cols-[minmax(0,1.1fr)_96px_120px_120px_minmax(140px,0.7fr)_minmax(130px,0.7fr)_minmax(150px,0.8fr)_80px] lg:items-center lg:rounded-none lg:border-0 lg:shadow-none">
                      <div className="min-w-0">
                        <Link to={`/liver/${row.id}`} className="group inline-flex min-w-0 items-center gap-2">
                          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sm font-semibold text-sky-700">
                            {row.displayName.slice(0, 1)}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium text-gray-900 group-hover:text-sky-700">
                              {row.displayName}
                            </span>
                            {row.isMine && (
                              <span className="mt-1 inline-flex rounded-full bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700">
                                自分の担当
                              </span>
                            )}
                          </span>
                        </Link>
                      </div>
                      <div>
                        <span className={`rounded-full px-2 py-1 text-xs ${STATUS_TONE[row.status]}`}>
                          {STATUS_LABEL[row.status]}
                        </span>
                      </div>
                      <SmallField label="LINE ID" value={row.lineUserId ?? '-'} />
                      <SmallField label="入所日" value={row.joinedAt ?? '-'} />
                      <SmallField
                        label="現担当"
                        value={row.currentManagerName}
                        note={row.currentManagerDepartment ?? undefined}
                      />
                      <HistoryField
                        hasCallHistory={row.hasCallHistory}
                        latestCallAt={row.latestCallAt}
                        hasLineHistory={row.hasLineHistory}
                        latestLineAt={row.latestLineAt}
                      />
                      <SmallField label="メモ" value={row.notePreview} />
                      <button
                        type="button"
                        onClick={() => setEditing(original)}
                        disabled={!original}
                        className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                        編集
                      </button>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="px-4 py-8 text-sm text-gray-400">
                条件に一致するライバーはいません。
              </p>
            )}
          </section>
        </div>
      </main>

      <LiverFormModal
        open={createOpen}
        mode="create"
        loading={createLiver.isPending}
        error={createLiver.error instanceof Error ? createLiver.error.message : null}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
      <LiverFormModal
        open={Boolean(editing)}
        mode="edit"
        initialLiver={editing}
        loading={updateLiver.isPending}
        error={updateLiver.error instanceof Error ? updateLiver.error.message : null}
        onClose={() => setEditing(null)}
        onSubmit={handleUpdate}
      />
    </div>
  )
}

function Metric({ label, value, tone }: { label: string; value: string; tone: 'slate' | 'emerald' | 'amber' | 'gray' }) {
  const toneClass = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    gray: 'bg-gray-100 text-gray-600'
  }[tone]

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${toneClass}`}>
          {label === '稼働中' ? <UserCheck className="h-4 w-4" /> : <Users className="h-4 w-4" />}
        </span>
      </div>
      <p className="mt-4 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
    </div>
  )
}

function SmallField({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="min-w-0 rounded-lg bg-gray-50 px-3 py-2 lg:bg-transparent lg:px-0 lg:py-0">
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className="mt-0.5 truncate text-sm text-gray-700">{value}</p>
      {note && <p className="mt-0.5 truncate text-[11px] text-gray-400">{note}</p>}
    </div>
  )
}

function HistoryField({
  hasCallHistory,
  latestCallAt,
  hasLineHistory,
  latestLineAt
}: {
  hasCallHistory: boolean
  latestCallAt: string | null
  hasLineHistory: boolean
  latestLineAt: string | null
}) {
  return (
    <div className="min-w-0 rounded-lg bg-gray-50 px-3 py-2 lg:bg-transparent lg:px-0 lg:py-0">
      <p className="text-[11px] text-gray-400">履歴</p>
      <div className="mt-1 flex flex-wrap gap-1">
        <HistoryBadge
          icon={<PhoneCall className="h-3 w-3" />}
          label="通話"
          active={hasCallHistory}
          date={latestCallAt}
        />
        <HistoryBadge
          icon={<MessageSquareText className="h-3 w-3" />}
          label="LINE"
          active={hasLineHistory}
          date={latestLineAt}
        />
      </div>
    </div>
  )
}

function HistoryBadge({
  icon,
  label,
  active,
  date
}: {
  icon: React.ReactNode
  label: string
  active: boolean
  date: string | null
}) {
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${
        active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-400'
      }`}
      title={date ? `${label}: ${formatDate(date)}` : `${label}: 履歴なし`}
    >
      {icon}
      {active ? formatDate(date) : 'なし'}
    </span>
  )
}

function formatDate(value: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return value
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`
}
